// Third Party
import * as React from 'react';
import { useToggle } from '@hooks';

// Utils
import { routes } from '@routes';
import { capitalizeFirstLetter } from '@utils/string-helpers';
import { AnalyticsRef } from '@components/analytics-ref';
import { appendUrlParams } from '@utils/append-url-params';

// UI
import { Avatar } from '@components/avatar';
import { Link } from '@components/link';
import { Typography } from '@components/typography';
import { RatingStars } from '@components/rating-stars';
import {
  AccountReviewSummary,
  useAccountCollectionListingsListAllQuery,
} from '@gql';
import { Icon, IconNames } from '@components/icon';
import { PhoneNumberModal } from './heading-and-title-bar/phone-number-modal';
import { PmListingsCarouselSection } from './pm-listings-carousel-section';
import { ExpandableText } from '@components/expandable-text';

type Props = {
  publicPhoneNumber?: string;
  publicEmail?: string;
  account: {
    id?: string;
    activeListingsCount?: number;
    avatarUrl?: string;
    city?: string;
    description?: string;
    companyName?: string;
    inBusinessSince?: number;
    stateOrProvinceLongName?: string;
    slug?: string;
    facebookPageUrl?: string;
    instagramUrl?: string;
    youtubeUrl?: string;
    twitterUrl?: string;
    pinterestUrl?: string;
    marketingWebsiteUrl?: string;
    linkedinUrl?: string;
    accountReviewSummaries?: AccountReviewSummary[];
  };
};

export const PropertyManagerBio: React.FC<Props> = ({
  publicPhoneNumber,
  publicEmail,
  account: {
    id,
    slug,
    description,
    avatarUrl,
    companyName,
    city,
    inBusinessSince,
    stateOrProvinceLongName,
    activeListingsCount,
    marketingWebsiteUrl,
    facebookPageUrl,
    instagramUrl,
    youtubeUrl,
    twitterUrl,
    pinterestUrl,
    linkedinUrl,
    accountReviewSummaries,
  },
}) => {
  const [page] = React.useState(1);

  const {
    on: phoneNumberModalIsOpen,
    setOff: closePhoneNumberModal,
    setOn: openPhoneNumberModal,
  } = useToggle();

  const { data: listingData } = useAccountCollectionListingsListAllQuery({
    variables: {
      accountId: id || '',
      pageSize: 10,
      page: page,
    },
  });

  return (
    <div>
      <div className="flex flex-row space-x-6 sm:space-x-4">
        <div className="relative aspect-1 flex flex-col items-center rounded-full">
          <div className="aspect-1 w-20 md:w-24 rounded-full shadow-md">
            <Avatar
              firstName={companyName}
              avatarUrl={avatarUrl}
              shape="circle"
              linkTo={routes.accounts(slug || '')}
            />
          </div>
          {accountReviewSummaries && accountReviewSummaries.length > 0 && (
            <div className="block sm:hidden mt-2">
              <RatingStars rating={accountReviewSummaries[0].rating} />
            </div>
          )}
        </div>

        <div className="flex flex-col sm:pl-4 w-full text-primary-dark sm:space-y-1 justify-center">
          <Typography
            component="h4"
            variant="subtitle2"
            variantM="title4"
            className="pb-1"
          >
            {companyName}
          </Typography>

          {city && stateOrProvinceLongName && (
            <Typography
              component="span"
              variant="body2"
              variantM="body1"
              className="text-primary-dark"
            >
              {`${capitalizeFirstLetter(city)}, ${capitalizeFirstLetter(
                stateOrProvinceLongName
              )}`}
            </Typography>
          )}

          {activeListingsCount !== undefined && activeListingsCount > 0 && (
            <Link
              to={routes.accounts(slug || '')}
              size="sm"
              className="text-sm"
            >
              {activeListingsCount === 1
                ? `${activeListingsCount} Property`
                : `${activeListingsCount.toLocaleString()} Properties`}
            </Link>
          )}

          {inBusinessSince !== null && (
            <Typography component="span" variant="body2">
              {`In business since ${inBusinessSince}`}
            </Typography>
          )}

          <div className="hidden sm:block mt-2">
            {accountReviewSummaries && accountReviewSummaries.length > 0 && (
              <RatingStars rating={accountReviewSummaries[0].rating} />
            )}
          </div>
        </div>

        <div className="w-full h-full items-end text-center hidden lg:flex lg:flex-col pt-2">
          {marketingWebsiteUrl && (
            <AnalyticsRef name="select_account_website">
              <Link
                size={'sm'}
                isButton={true}
                isFullWidth={true}
                to={appendUrlParams(marketingWebsiteUrl, {
                  utm_source: 'Savvy',
                  utm_medium: 'website',
                })}
                target="_blank"
                rel="noreferrer"
                color="primary"
                variant="outlined"
              >
                Visit Website
              </Link>
            </AnalyticsRef>
          )}

          <div className="flex overflow-x-auto w-full justify-end text-primary-main space-x-2 items-end pt-4">
            {publicPhoneNumber && (
              <div>
                <PhoneNumberModal
                  isOpen={phoneNumberModalIsOpen}
                  closeModal={closePhoneNumberModal}
                  phoneNumber={publicPhoneNumber}
                />
                <AnalyticsRef name="select_listing_phone">
                  <button
                    style={{ outline: 'none' }}
                    onClick={openPhoneNumberModal}
                  >
                    <Icon
                      size="lg"
                      name={IconNames.PHONE}
                      className="w-7 h-7 hover:opacity-80"
                    />
                  </button>
                </AnalyticsRef>
              </div>
            )}
            {publicEmail && (
              <AnalyticsRef name="select_listing_email">
                <a href={`mailto:${publicEmail}?subject=Inquiry from Savvy`}>
                  <Icon
                    size="lg"
                    name={IconNames.ENVELOPE}
                    className="w-7 h-7 hover:opacity-80"
                  />
                </a>
              </AnalyticsRef>
            )}
            {facebookPageUrl && (
              <AnalyticsRef name="select_listing_social">
                <a
                  href={facebookPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon
                    size="lg"
                    name={IconNames.FACEBOOK}
                    className="w-7 h-7 hover:opacity-80"
                  />
                </a>
              </AnalyticsRef>
            )}
            {instagramUrl && (
              <AnalyticsRef name="select_listing_social">
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon
                    size="lg"
                    name={IconNames.INSTAGRAM}
                    className="w-7 h-7 hover:opacity-80"
                  />
                </a>
              </AnalyticsRef>
            )}
            {youtubeUrl && (
              <AnalyticsRef name="select_listing_social">
                <a href={youtubeUrl} target="_blank" rel="noopener noreferrer">
                  <Icon
                    size="lg"
                    name={IconNames.YOUTUBE}
                    className="w-7 h-7 hover:opacity-80"
                  />
                </a>
              </AnalyticsRef>
            )}
            {twitterUrl && (
              <AnalyticsRef name="select_listing_social">
                <a href={twitterUrl} target="_blank" rel="noopener noreferrer">
                  <Icon
                    size="lg"
                    name={IconNames.TWITTERX}
                    className="w-7 h-7 hover:opacity-80"
                  />
                </a>
              </AnalyticsRef>
            )}
            {pinterestUrl && (
              <AnalyticsRef name="select_listing_social">
                <a
                  href={pinterestUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon
                    size="lg"
                    name={IconNames.PINTEREST}
                    className="w-7 h-7 hover:opacity-80"
                  />
                </a>
              </AnalyticsRef>
            )}
            {linkedinUrl && (
              <AnalyticsRef name="select_listing_social">
                <a href={linkedinUrl} target="_blank" rel="noopener noreferrer">
                  <Icon
                    size="lg"
                    name={IconNames.LINKEDIN}
                    className="w-7 h-7 hover:opacity-80"
                  />
                </a>
              </AnalyticsRef>
            )}
          </div>
        </div>
      </div>
      <div className="pt-2 sm:pt-4">
        <div>
          {description && (
            <ExpandableText
              text={description}
              maxLines={8}
              testId="@listing/about-property"
              typographyVariant="body2"
              typographyVariantM="body2"
              className="text-primary-main"
              buttonColor="primary"
              buttonVariant="text"
              buttonClassName="underline"
            />
          )}
        </div>
        <div className="w-full">
          {listingData?.listingsListAllActive.entries && (
            <PmListingsCarouselSection
              listings={listingData?.listingsListAllActive.entries}
              accountSlug={slug}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col w-full h-full items-end text-center lg:hidden pt-4 lg:pt-2">
        <div className="flex overflow-x-auto w-full justify-start text-primary-main space-x-2 items-end">
          {publicPhoneNumber && (
            <div>
              <PhoneNumberModal
                isOpen={phoneNumberModalIsOpen}
                closeModal={closePhoneNumberModal}
                phoneNumber={publicPhoneNumber}
              />
              <AnalyticsRef name="select_listing_phone">
                <button
                  style={{ outline: 'none' }}
                  onClick={openPhoneNumberModal}
                >
                  <Icon
                    size="lg"
                    name={IconNames.PHONE}
                    className="w-7 h-7 hover:opacity-80"
                  />
                </button>
              </AnalyticsRef>
            </div>
          )}
          {publicEmail && (
            <AnalyticsRef name="select_listing_email">
              <a href={`mailto:${publicEmail}?subject=Inquiry from Savvy`}>
                <Icon
                  size="lg"
                  name={IconNames.ENVELOPE}
                  className="w-7 h-7 hover:opacity-80"
                />
              </a>
            </AnalyticsRef>
          )}
          {facebookPageUrl && (
            <AnalyticsRef name="select_listing_social">
              <a
                href={facebookPageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon
                  size="lg"
                  name={IconNames.FACEBOOK}
                  className="w-7 h-7 hover:opacity-80"
                />
              </a>
            </AnalyticsRef>
          )}
          {instagramUrl && (
            <AnalyticsRef name="select_listing_social">
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer">
                <Icon
                  size="lg"
                  name={IconNames.INSTAGRAM}
                  className="w-7 h-7 hover:opacity-80"
                />
              </a>
            </AnalyticsRef>
          )}
          {youtubeUrl && (
            <AnalyticsRef name="select_listing_social">
              <a href={youtubeUrl} target="_blank" rel="noopener noreferrer">
                <Icon
                  size="lg"
                  name={IconNames.YOUTUBE}
                  className="w-7 h-7 hover:opacity-80"
                />
              </a>
            </AnalyticsRef>
          )}
          {twitterUrl && (
            <AnalyticsRef name="select_listing_social">
              <a href={twitterUrl} target="_blank" rel="noopener noreferrer">
                <Icon
                  size="lg"
                  name={IconNames.TWITTERX}
                  className="w-7 h-7 hover:opacity-80"
                />
              </a>
            </AnalyticsRef>
          )}
          {pinterestUrl && (
            <AnalyticsRef name="select_listing_social">
              <a href={pinterestUrl} target="_blank" rel="noopener noreferrer">
                <Icon
                  size="lg"
                  name={IconNames.PINTEREST}
                  className="w-7 h-7 hover:opacity-80"
                />
              </a>
            </AnalyticsRef>
          )}
          {linkedinUrl && (
            <AnalyticsRef name="select_listing_social">
              <a href={linkedinUrl} target="_blank" rel="noopener noreferrer">
                <Icon
                  size="lg"
                  name={IconNames.LINKEDIN}
                  className="w-7 h-7 hover:opacity-80"
                />
              </a>
            </AnalyticsRef>
          )}
        </div>
      </div>
    </div>
  );
};
